import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ModalShell from './ModalShell';
import { updateAttendancePunch } from '../../../../redux/actions/SocietyPortal/AttendanceAction/AttendanceAction';
import { toLocalInputValue, fromLocalInputValue, nowLocalInputValue } from './datetime';

// Correct an existing punch — PATCH /api/attendance/:id (attendance.manage).
// Sends only what changed plus the mandatory update_reason. Editing a
// PENDING_APPROVAL punch keeps it pending; REJECTED punches can't be edited
// (409 PUNCH_REJECTED) — record a replacement via the manual-punch modal.
const EditPunchModal = ({ punch, onClose, onDone }) => {
    const dispatch = useDispatch();
    const { updatePunchLoading } = useSelector(state => state.AttendanceReducer || {});

    const [punchType, setPunchType] = useState('IN');
    const [punchAt, setPunchAt] = useState('');
    const [notes, setNotes] = useState('');
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (!punch) return;
        setPunchType(punch.punch_type || 'IN');
        setPunchAt(toLocalInputValue(punch.punch_at));
        setNotes(punch.notes || '');
        setReason('');
    }, [punch]);

    if (!punch) return null;

    const handleSubmit = () => {
        if (reason.trim().length < 3) return toast.warn('Update reason must be at least 3 characters');

        const payload = { punch_id: punch.punch_id, update_reason: reason.trim() };
        if (punchType !== punch.punch_type) payload.punch_type = punchType;
        const punchAtIso = fromLocalInputValue(punchAt);
        if (punchAtIso && toLocalInputValue(punch.punch_at) !== punchAt) {
            if (new Date(punchAtIso) > new Date()) return toast.warn('Punch time cannot be in the future');
            payload.punch_at = punchAtIso;
        }
        if ((notes || '') !== (punch.notes || '')) payload.notes = notes;

        if (!payload.punch_type && !payload.punch_at && payload.notes === undefined) {
            return toast.info('Nothing changed');
        }

        dispatch(updateAttendancePunch({
            ...payload,
            onSuccess: (result) => {
                onClose();
                // result: { punch, attendance, previous_day } — previous_day is
                // non-null when the new time crossed an attendance-day
                // boundary; the grid must refresh that row too.
                onDone?.(result);
            },
        }));
    };

    const inputClass = 'tw-w-full tw-border tw-border-gray-200 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500';

    return (
        <ModalShell
            isOpen={!!punch}
            title={`Edit punch — ${punch.employee?.name || `#${punch.punch_id}`}`}
            icon="mdi:pencil-outline"
            onClose={onClose}
            busy={updatePunchLoading}
            actions={
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={updatePunchLoading}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-1.5 tw-rounded-md tw-bg-blue-600 tw-text-white tw-text-xs tw-font-medium hover:tw-bg-blue-700 disabled:tw-opacity-50"
                >
                    {updatePunchLoading ? <Icon icon="mdi:loading" className="tw-animate-spin" /> : <Icon icon="mdi:check" />}
                    Save changes
                </button>
            }
        >
            <div className="tw-flex tw-flex-col tw-gap-3">
                {punch.status === 'PENDING_APPROVAL' && (
                    <p className="tw-text-[11px] tw-text-blue-700 tw-bg-blue-50 tw-rounded-md tw-p-2 tw-m-0 tw-flex tw-items-start tw-gap-1.5">
                        <Icon icon="mdi:information-outline" className="tw-flex-shrink-0 tw-mt-0.5" />
                        This punch stays pending after the edit — approving or rejecting it is a separate decision.
                    </p>
                )}
                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                    <div>
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Direction</label>
                        <select value={punchType} onChange={(e) => setPunchType(e.target.value)} className={inputClass}>
                            <option value="IN">IN — start of shift</option>
                            <option value="OUT">OUT — end of shift</option>
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Punch time</label>
                        <input
                            type="datetime-local"
                            value={punchAt}
                            max={nowLocalInputValue()}
                            onChange={(e) => setPunchAt(e.target.value)}
                            className={inputClass}
                        />
                    </div>
                </div>
                <div>
                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Notes</label>
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={1000} className={inputClass} />
                </div>
                <div>
                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Update reason <span className="tw-text-red-500">*</span></label>
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={2}
                        maxLength={255}
                        placeholder="e.g. Tapped IN instead of OUT at shift end (shown in the audit trail)"
                        className={inputClass}
                    />
                </div>
            </div>
        </ModalShell>
    );
};

export default EditPunchModal;
